import React, { useState, useEffect, useMemo } from 'react';
import { staffApi } from '../../services/api';
import toast from 'react-hot-toast';
import { HiCheckCircle, HiCurrencyRupee, HiEye, HiX, HiDownload, HiPencil, HiTrash } from 'react-icons/hi';
import {
  getCalendarNowIST,
  formatMonthLongFromIndex0,
  todayYmdIST,
} from '../../utils/timezoneDisplay';
import { useClinic } from '../../context/ClinicContext';
import { resolveClinicLogoSrc, defaultClinicLogo } from '../../utils/clinicLogo';

const formatInr = (value) => {
  const n = parseFloat(value);
  if (!Number.isFinite(n)) return '₹0.00';
  return `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/** Public clinic logo via shared helper (signed URLs come from API when available). */
const SLIP_BLUE = 'bg-[#4A69E1]';
/** Section label flush left; body uses SLIP_CONTENT_INDENT (~one tab) */
const SLIP_SECTION_HEAD =
  'bg-slate-50 border-b border-slate-200 py-2.5 pl-0 pr-4 text-left text-xs font-semibold uppercase tracking-wide text-slate-600';
const SLIP_CONTENT_INDENT = 'pl-8 pr-4 sm:pr-5';

const DEFAULT_CLINIC_PAYSLIP_ADDRESS =
  'Shop No GF 5-6, Omkar Lotus, 76/B, T P Rd, opposite swaminarayan temple, Nigam Nagar, Chandkheda, Ahmedabad, Gujarat 382424';
const DEFAULT_CLINIC_PAYSLIP_PHONE = '8758383643';

const Payroll = () => {
  const { currentClinic } = useClinic();
  const [payroll, setPayroll] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(() => getCalendarNowIST().month);
  const [selectedYear, setSelectedYear] = useState(() => getCalendarNowIST().year);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateData, setGenerateData] = useState(() => {
    const { month, year } = getCalendarNowIST();
    return {
      user_id: '',
      month,
      year,
      basic_salary: '',
      allowances: '',
      deductions: '',
      bonus: '',
    };
  });

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, selectedYear]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [payrollRes, staffRes] = await Promise.all([
        staffApi.getPayroll({ month: selectedMonth, year: selectedYear }),
        staffApi.getAll()
      ]);
      setPayroll(payrollRes.data.data);
      setStaff(staffRes.data.data);
    } catch (error) {
      toast.error('Failed to fetch payroll data');
    } finally {
      setLoading(false);
    }
  };

  const [showBreakdownModal, setShowBreakdownModal] = useState(false);
  const [breakdownData, setBreakdownData] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewPayroll, setViewPayroll] = useState(null);
  const [viewBreakdown, setViewBreakdown] = useState(null);
  const [loadingBreakdown, setLoadingBreakdown] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPayroll, setEditingPayroll] = useState(null);
  const [editData, setEditData] = useState({
    basic_salary: '',
    allowances: '',
    deductions: '',
    bonus: '',
    recalculate_from_attendance: true,
  });
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [leavePreview, setLeavePreview] = useState(null);
  const [leavePreviewLoading, setLeavePreviewLoading] = useState(false);
  const [leaveOverflowSettlement, setLeaveOverflowSettlement] = useState('use_carry_forward');

  const effectiveLeavePreview = useMemo(() => {
    if (!leavePreview) return null;
    if (!leavePreview.requires_overflow_choice || !leavePreview.settlement_options) {
      return leavePreview;
    }
    const selected =
      leavePreview.settlement_options[leaveOverflowSettlement]
      || leavePreview.settlement_options.use_carry_forward;
    if (!selected) return leavePreview;
    return {
      ...leavePreview,
      leaveDeduction: selected.leave_deduction,
      excess_leave_days: selected.excess_leave_days,
      excess_leave_deduction: selected.excess_leave_deduction,
      leave_overflow_settlement: leaveOverflowSettlement,
      breakdown: {
        ...leavePreview.breakdown,
        excess_leave_days: selected.excess_leave_days,
        excess_leave_deduction: selected.excess_leave_deduction,
        leave_overflow_settlement: leaveOverflowSettlement,
        monthly_leave_balance: leavePreview.breakdown?.monthly_leave_balance
          ? {
              ...leavePreview.breakdown.monthly_leave_balance,
              excess_days: selected.excess_leave_days,
              carry_forward_out: selected.carry_forward_out,
              overflow_settlement: leaveOverflowSettlement,
            }
          : null,
      },
    };
  }, [leavePreview, leaveOverflowSettlement]);

  useEffect(() => {
    if (!showGenerateModal) {
      setLeavePreview(null);
      setLeaveOverflowSettlement('use_carry_forward');
      return;
    }
    const userId = parseInt(generateData.user_id, 10);
    const basic = parseFloat(generateData.basic_salary);
    if (!userId || !generateData.month || !generateData.year || !Number.isFinite(basic) || basic <= 0) {
      setLeavePreview(null);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLeavePreviewLoading(true);
      try {
        const res = await staffApi.previewPayrollLeaveDeduction({
          user_id: userId,
          month: generateData.month,
          year: generateData.year,
          basic_salary: basic,
        });
        if (!cancelled) setLeavePreview(res.data?.data || null);
      } catch {
        if (!cancelled) setLeavePreview(null);
      } finally {
        if (!cancelled) setLeavePreviewLoading(false);
      }
    };

    const t = setTimeout(load, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [
    showGenerateModal,
    generateData.user_id,
    generateData.month,
    generateData.year,
    generateData.basic_salary,
  ]);

  const handleGenerate = async () => {
    try {
      const payload = {
        ...generateData,
        ...(leavePreview?.requires_overflow_choice
          ? { leave_overflow_settlement: leaveOverflowSettlement }
          : {}),
      };
      const response = await staffApi.generatePayroll(payload);
      toast.success('Payroll generated successfully');
      setShowGenerateModal(false);
      setLeaveOverflowSettlement('use_carry_forward');
      
      // Show breakdown modal if breakdown data is available
      if (response.data.data?.breakdown?.leave_type_breakdown) {
        setBreakdownData({
          staffName: staff.find(s => s.id === parseInt(generateData.user_id))?.full_name || 'Staff',
          period: `${formatMonthLongFromIndex0(generateData.month - 1)} ${generateData.year}`,
          breakdown: response.data.data.breakdown,
          perDaySalary: response.data.data.breakdown.per_day_salary
        });
        setShowBreakdownModal(true);
      }
      
      setGenerateData(() => {
        const { month, year } = getCalendarNowIST();
        return {
          user_id: '',
          month,
          year,
          basic_salary: '',
          allowances: '',
          deductions: '',
          bonus: '',
        };
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to generate payroll');
    }
  };

  const handleDownloadSlip = async (id) => {
    try {
      const response = await staffApi.getPaySlip(id, 'pdf');
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `PaySlip_${selectedMonth}_${selectedYear}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Payslip PDF downloaded');
    } catch (error) {
      toast.error('Failed to download payslip');
    }
  };

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [paymentData, setPaymentData] = useState({
    payment_status: 'paid',
    payment_date: todayYmdIST(),
    payment_mode: 'bank_transfer',
    notes: ''
  });

  const handleMarkAsPaid = (payroll) => {
    setSelectedPayroll(payroll);
    setPaymentData({
      payment_status: 'paid',
      payment_date: todayYmdIST(),
      payment_mode: payroll.payment_mode || 'bank_transfer',
      notes: payroll.notes || ''
    });
    setShowPaymentModal(true);
  };

  const handleUpdatePayment = async () => {
    try {
      const res = await staffApi.updatePayroll(selectedPayroll.id, paymentData);
      const expense = res.data?.data?.expense;
      if (expense?.created) {
        toast.success(
          `Marked as paid. An expense was added under Staff Salary for ₹${parseFloat(selectedPayroll.net_salary).toFixed(2)} on ${paymentData.payment_date}.`
        );
      } else if (expense?.duplicate) {
        toast.success('Marked as paid. Expense for this payroll was already recorded.');
      } else if (expense?.error) {
        toast.success('Payroll marked as paid.');
        toast.error(`Could not add expense automatically: ${expense.error}`);
      } else {
        toast.success('Payment status updated');
      }
      setShowPaymentModal(false);
      setSelectedPayroll(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update payment status');
    }
  };

  const handleEditPayroll = (item) => {
    setEditingPayroll(item);
    setEditData({
      basic_salary: String(item.basic_salary ?? ''),
      allowances: String(item.allowances ?? '0'),
      deductions: String(item.deductions ?? '0'),
      bonus: String(item.bonus ?? '0'),
      recalculate_from_attendance: false,
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editingPayroll?.id) return;
    const basic = parseFloat(editData.basic_salary);
    if (!Number.isFinite(basic) || basic < 0) {
      toast.error('Enter a valid basic salary');
      return;
    }
    setSavingEdit(true);
    try {
      const response = await staffApi.updatePayroll(editingPayroll.id, {
        basic_salary: basic,
        allowances: parseFloat(editData.allowances) || 0,
        deductions: parseFloat(editData.deductions) || 0,
        bonus: parseFloat(editData.bonus) || 0,
        recalculate_from_attendance: editData.recalculate_from_attendance,
      });
      const breakdown = response.data?.data?.breakdown;
      toast.success('Payroll updated');
      setShowEditModal(false);
      setEditingPayroll(null);
      if (breakdown?.leave_type_breakdown?.length) {
        setBreakdownData({
          staffName: editingPayroll.full_name,
          period: `${formatMonthLongFromIndex0(editingPayroll.month - 1)} ${editingPayroll.year}`,
          breakdown,
          perDaySalary: breakdown.per_day_salary,
        });
        setShowBreakdownModal(true);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update payroll');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeletePayroll = async (item) => {
    if (!item?.id) return;
    const period = `${formatMonthLongFromIndex0(item.month - 1)} ${item.year}`;
    const paidWarning =
      item.payment_status === 'paid'
        ? '\n\nThis payroll was marked paid. Any linked salary expense will also be removed.'
        : '';
    if (
      !window.confirm(
        `Delete payroll for ${item.full_name} (${period})? This cannot be undone.${paidWarning}`
      )
    ) {
      return;
    }
    setDeletingId(item.id);
    try {
      await staffApi.deletePayroll(item.id);
      toast.success('Payroll deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete payroll');
    } finally {
      setDeletingId(null);
    }
  };

  const handleViewPayroll = async (payrollItem) => {
    setViewBreakdown(null);
    setViewPayroll(payrollItem);
    setLoadingBreakdown(true);
    setShowViewModal(true);
    try {
      const response = await staffApi.getPayrollById(payrollItem.id);
      if (response.data.success && response.data.data) {
        const d = response.data.data;
        const { breakdown, ...fields } = d;
        setViewPayroll({ ...payrollItem, ...fields });
        setViewBreakdown(breakdown ?? null);
      } else {
        setViewBreakdown(null);
      }
    } catch (error) {
      console.error('Failed to fetch payroll breakdown:', error);
      toast.error('Failed to load payroll breakdown');
    } finally {
      setLoadingBreakdown(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 ring-1 ring-inset ring-emerald-600/20">
            Paid
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-900 ring-1 ring-inset ring-amber-600/20">
            Pending
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 ring-1 ring-inset ring-slate-500/15 capitalize">
            {status}
          </span>
        );
    }
  };

  const slipPeriodLabel = useMemo(() => {
    if (!viewPayroll) return '';
    return `${formatMonthLongFromIndex0(viewPayroll.month - 1)} ${viewPayroll.year}`;
  }, [viewPayroll]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Payroll Management</h2>
          <p className="text-sm text-gray-500">Generate and manage staff salaries</p>
        </div>
        <div className="flex gap-3">
          <div className="flex gap-2">
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {formatMonthLongFromIndex0(i)}
                </option>
              ))}
            </select>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            >
              {Array.from({ length: 5 }, (_, i) => {
                const year = new Date().getFullYear() - 2 + i;
                return <option key={year} value={year}>{year}</option>;
              })}
            </select>
          </div>
          <button
            onClick={() => setShowGenerateModal(true)}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center gap-2"
          >
            <HiCurrencyRupee className="w-5 h-5" />
            Generate Payroll
          </button>
        </div>
      </div>

      {/* Payroll List */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left p-3 font-semibold text-gray-700">Staff</th>
                <th className="text-left p-3 font-semibold text-gray-700">Period</th>
                <th className="text-right p-3 font-semibold text-gray-700">Basic Salary</th>
                <th className="text-right p-3 font-semibold text-gray-700">Allowances</th>
                <th className="text-right p-3 font-semibold text-gray-700">Deductions</th>
                <th className="text-right p-3 font-semibold text-gray-700">Leave Deduction</th>
                <th className="text-right p-3 font-semibold text-gray-700">Bonus</th>
                <th className="text-right p-3 font-semibold text-gray-700">Net Salary</th>
                <th className="text-center p-3 font-semibold text-gray-700">Status</th>
                <th className="text-center p-3 font-semibold text-gray-700">Payment Mode</th>
                <th className="text-center p-3 font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {payroll.length === 0 ? (
                <tr>
                  <td colSpan="11" className="text-center p-8 text-gray-500">
                    No payroll records found for this period
                  </td>
                </tr>
              ) : (
                payroll.map((item) => (
                  <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="p-3">
                      <div className="font-medium text-gray-900">{item.full_name}</div>
                      <div className="text-sm text-gray-500">{item.email}</div>
                    </td>
                    <td className="p-3 text-sm text-gray-600">
                      {formatMonthLongFromIndex0(item.month - 1)} {item.year}
                    </td>
                    <td className="p-3 text-right font-medium">₹{parseFloat(item.basic_salary).toFixed(2)}</td>
                    <td className="p-3 text-right">₹{parseFloat(item.allowances || 0).toFixed(2)}</td>
                    <td className="p-3 text-right text-red-600">-₹{parseFloat(item.deductions || 0).toFixed(2)}</td>
                    <td className="p-3 text-right text-red-600">
                      -₹{parseFloat(item.leave_deduction || 0).toFixed(2)}
                      <span className="text-xs text-gray-500 ml-1">({item.leaves_taken} days)</span>
                    </td>
                    <td className="p-3 text-right text-green-600">+₹{parseFloat(item.bonus || 0).toFixed(2)}</td>
                    <td className="p-3 text-right font-semibold text-primary-600">
                      ₹{parseFloat(item.net_salary).toFixed(2)}
                    </td>
                    <td className="p-3 text-center">{getStatusBadge(item.payment_status)}</td>
                    <td className="p-3 text-center">
                      {item.payment_mode ? (
                        <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded capitalize">
                          {item.payment_mode.replace('_', ' ')}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleViewPayroll(item)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                          title="View Details"
                        >
                          <HiEye className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDownloadSlip(item.id)}
                          className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg"
                          title="Download payslip (PDF from Word layout)"
                        >
                          <HiDownload className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleEditPayroll(item)}
                          className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg"
                          title="Edit payroll"
                        >
                          <HiPencil className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDeletePayroll(item)}
                          disabled={deletingId === item.id}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50"
                          title="Delete payroll"
                        >
                          <HiTrash className="w-5 h-5" />
                        </button>
                        {item.payment_status === 'pending' && (
                          <button
                            onClick={() => handleMarkAsPaid(item)}
                            className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                            title="Mark as Paid"
                          >
                            <HiCheckCircle className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Payroll Modal */}
      {showEditModal && editingPayroll && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold">Edit Payroll</h3>
              <p className="text-sm text-gray-500 mt-1">
                {editingPayroll.full_name} — {formatMonthLongFromIndex0(editingPayroll.month - 1)} {editingPayroll.year}
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm bg-gray-50 rounded-lg p-3">
                <div>
                  <span className="text-gray-500">Leave deduction</span>
                  <p className="font-medium text-red-600">
                    ₹{parseFloat(editingPayroll.leave_deduction || 0).toFixed(2)}
                    <span className="text-xs text-gray-500 ml-1">({editingPayroll.leaves_taken} days)</span>
                  </p>
                </div>
                <div>
                  <span className="text-gray-500">Current net salary</span>
                  <p className="font-medium text-primary-600">
                    ₹{parseFloat(editingPayroll.net_salary || 0).toFixed(2)}
                  </p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Basic Salary (₹)</label>
                <input
                  type="number"
                  value={editData.basic_salary}
                  onChange={(e) => setEditData({ ...editData, basic_salary: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Allowances (₹)</label>
                  <input
                    type="number"
                    value={editData.allowances}
                    onChange={(e) => setEditData({ ...editData, allowances: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Deductions (₹)</label>
                  <input
                    type="number"
                    value={editData.deductions}
                    onChange={(e) => setEditData({ ...editData, deductions: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bonus (₹)</label>
                <input
                  type="number"
                  value={editData.bonus}
                  onChange={(e) => setEditData({ ...editData, bonus: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editData.recalculate_from_attendance}
                  onChange={(e) =>
                    setEditData({ ...editData, recalculate_from_attendance: e.target.checked })
                  }
                  className="mt-1 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">
                  Recalculate leave deduction from attendance for this month
                </span>
              </label>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowEditModal(false);
                  setEditingPayroll(null);
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveEdit}
                disabled={savingEdit || !editData.basic_salary}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {savingEdit ? 'Saving...' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Generate Payroll Modal */}
      {showGenerateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-lg font-semibold">Generate Payroll</h3>
            </div>
            <div className="p-6 space-y-4 overflow-y-auto flex-1 min-h-0">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Staff Member</label>
                <select
                  value={generateData.user_id}
                  onChange={async (e) => {
                    const selectedStaffId = e.target.value;
                    
                    if (!selectedStaffId) {
                      // Clear all fields if no staff selected
                      setGenerateData({
                        ...generateData,
                        user_id: '',
                        basic_salary: '',
                        allowances: '',
                        deductions: '',
                        bonus: ''
                      });
                      return;
                    }
                    
                    const selectedStaff = staff.find(s => s.id === parseInt(selectedStaffId));
                    let salaryInfo = null;
                    
                    // First try to get from staff list
                    if (selectedStaff && selectedStaff.salary_info) {
                      salaryInfo = selectedStaff.salary_info;
                    }
                    
                    // If not found in list, fetch from API
                    if (!salaryInfo && selectedStaffId) {
                      try {
                        const response = await staffApi.getById(selectedStaffId);
                        if (response.data.success && response.data.data.salary_info) {
                          salaryInfo = response.data.data.salary_info;
                        }
                      } catch (error) {
                        console.error('Failed to fetch staff salary info:', error);
                      }
                    }
                    
                    // Auto-populate salary fields
                    if (salaryInfo && salaryInfo.basic_salary) {
                      const basicSalary = parseFloat(salaryInfo.basic_salary) || 0;
                      const allowances = parseFloat(salaryInfo.allowances) || 0;
                      const deductions = parseFloat(salaryInfo.deductions) || 0;
                      const bonus = parseFloat(salaryInfo.bonus) || 0;
                      
                      setGenerateData({
                        ...generateData,
                        user_id: selectedStaffId,
                        basic_salary: basicSalary > 0 ? String(basicSalary) : '',
                        allowances: allowances > 0 ? String(allowances) : '',
                        deductions: deductions > 0 ? String(deductions) : '',
                        bonus: bonus > 0 ? String(bonus) : ''
                      });
                    } else {
                      // Clear fields if no salary info found
                      setGenerateData({
                        ...generateData,
                        user_id: selectedStaffId,
                        basic_salary: '',
                        allowances: '',
                        deductions: '',
                        bonus: ''
                      });
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">Select Staff</option>
                  {staff.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.full_name} ({s.role})
                      {s.salary_info?.basic_salary ? ` - ₹${parseFloat(s.salary_info.basic_salary).toFixed(0)}` : ''}
                    </option>
                  ))}
                </select>
                {generateData.user_id && (() => {
                  const selectedStaff = staff.find(s => s.id === parseInt(generateData.user_id));
                  let salaryInfo = selectedStaff?.salary_info;
                  
                  // If not in list, try to get from API (for display only)
                  if (!salaryInfo && generateData.user_id) {
                    // We'll show the info from generateData instead
                    if (generateData.basic_salary) {
                      return (
                        <p className="text-xs text-green-600 mt-1 font-medium">
                          ✓ Salary loaded: Basic ₹{parseFloat(generateData.basic_salary).toFixed(2)}
                          {generateData.allowances && parseFloat(generateData.allowances) > 0 && ` + Allowances ₹${parseFloat(generateData.allowances).toFixed(2)}`}
                          {generateData.deductions && parseFloat(generateData.deductions) > 0 && ` - Deductions ₹${parseFloat(generateData.deductions).toFixed(2)}`}
                        </p>
                      );
                    }
                  }
                  
                  return salaryInfo?.basic_salary ? (
                    <p className="text-xs text-gray-500 mt-1">
                      Last salary: Basic ₹{parseFloat(salaryInfo.basic_salary).toFixed(2)}
                      {salaryInfo.allowances > 0 && ` + Allowances ₹${parseFloat(salaryInfo.allowances).toFixed(2)}`}
                      {salaryInfo.deductions > 0 && ` - Deductions ₹${parseFloat(salaryInfo.deductions).toFixed(2)}`}
                    </p>
                  ) : generateData.basic_salary ? (
                    <p className="text-xs text-green-600 mt-1 font-medium">
                      ✓ Salary loaded: Basic ₹{parseFloat(generateData.basic_salary).toFixed(2)}
                    </p>
                  ) : (
                    <p className="text-xs text-gray-400 mt-1">No previous salary record found</p>
                  );
                })()}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Month</label>
                  <select
                    value={generateData.month}
                    onChange={(e) => setGenerateData({ ...generateData, month: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    {Array.from({ length: 12 }, (_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {formatMonthLongFromIndex0(i)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                  <input
                    type="number"
                    value={generateData.year}
                    onChange={(e) => setGenerateData({ ...generateData, year: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Basic Salary (₹)</label>
                <input
                  type="number"
                  value={generateData.basic_salary}
                  onChange={(e) => setGenerateData({ ...generateData, basic_salary: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Enter basic salary"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Allowances (₹)</label>
                  <input
                    type="number"
                    value={generateData.allowances}
                    onChange={(e) => setGenerateData({ ...generateData, allowances: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Deductions (₹)</label>
                  <input
                    type="number"
                    value={generateData.deductions}
                    onChange={(e) => setGenerateData({ ...generateData, deductions: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="0"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bonus (₹)</label>
                <input
                  type="number"
                  value={generateData.bonus}
                  onChange={(e) => setGenerateData({ ...generateData, bonus: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="0"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Leave within monthly balance is paid. If leave used exceeds this month&apos;s credit but
                  carry-forward can cover it, choose at generate time whether overflow uses previous quota
                  or salary deduction. During probation, any leave is deducted from salary.
                </p>
              </div>

              {(leavePreviewLoading || effectiveLeavePreview) && (
                <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm space-y-2">
                  <p className="font-semibold text-gray-900">Leave deduction preview</p>
                  {leavePreviewLoading ? (
                    <p className="text-gray-500 text-xs">Calculating…</p>
                  ) : effectiveLeavePreview?.breakdown?.monthly_leave_balance ? (
                    <>
                      {effectiveLeavePreview.breakdown.monthly_leave_balance.in_probation && (
                        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                          Employee is in probation — leave cannot be used from balance; all leave days deduct salary.
                        </p>
                      )}
                      {effectiveLeavePreview.requires_overflow_choice && (
                        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-3">
                          <p className="text-xs font-semibold text-amber-900">
                            Leave used exceeds this month&apos;s credit by{' '}
                            <strong>{parseFloat(effectiveLeavePreview.monthly_overflow_days || 0).toFixed(1)}</strong>{' '}
                            day{effectiveLeavePreview.monthly_overflow_days === 1 ? '' : 's'}. Choose how to handle
                            the overflow:
                          </p>
                          <div className="grid grid-cols-2 gap-2 text-xs text-amber-900">
                            <div>
                              Carry-forward in:{' '}
                              <strong>{parseFloat(effectiveLeavePreview.carry_in || 0).toFixed(1)}</strong>
                            </div>
                            <div>
                              Monthly credit:{' '}
                              <strong>
                                {parseFloat(
                                  effectiveLeavePreview.breakdown.monthly_leave_balance.monthly_credit || 0
                                ).toFixed(1)}
                              </strong>
                            </div>
                            <div>
                              Used:{' '}
                              <strong>
                                {parseFloat(
                                  effectiveLeavePreview.breakdown.monthly_leave_balance.used_days || 0
                                ).toFixed(1)}
                              </strong>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <label className="flex cursor-pointer items-start gap-2 rounded border border-amber-200 bg-white p-2">
                              <input
                                type="radio"
                                name="leaveOverflowSettlement"
                                value="use_carry_forward"
                                checked={leaveOverflowSettlement === 'use_carry_forward'}
                                onChange={() => setLeaveOverflowSettlement('use_carry_forward')}
                                className="mt-0.5"
                              />
                              <span>
                                <span className="font-medium text-gray-900">Use previous leave quota</span>
                                <span className="block text-gray-600">
                                  Deduct overflow from carry-forward. Carry-out after payroll:{' '}
                                  <strong>
                                    {parseFloat(
                                      leavePreview?.settlement_options?.use_carry_forward?.carry_forward_out || 0
                                    ).toFixed(1)}
                                  </strong>{' '}
                                  days. No salary deduction for overflow.
                                </span>
                              </span>
                            </label>
                            <label className="flex cursor-pointer items-start gap-2 rounded border border-amber-200 bg-white p-2">
                              <input
                                type="radio"
                                name="leaveOverflowSettlement"
                                value="salary_deduct"
                                checked={leaveOverflowSettlement === 'salary_deduct'}
                                onChange={() => setLeaveOverflowSettlement('salary_deduct')}
                                className="mt-0.5"
                              />
                              <span>
                                <span className="font-medium text-gray-900">Deduct from salary</span>
                                <span className="block text-gray-600">
                                  Keep carry-forward unchanged. Salary deduction for overflow:{' '}
                                  <strong className="text-red-600">
                                    {formatInr(
                                      leavePreview?.settlement_options?.salary_deduct?.excess_leave_deduction || 0
                                    )}
                                  </strong>
                                  . Carry-out after payroll:{' '}
                                  <strong>
                                    {parseFloat(
                                      leavePreview?.settlement_options?.salary_deduct?.carry_forward_out || 0
                                    ).toFixed(1)}
                                  </strong>{' '}
                                  days.
                                </span>
                              </span>
                            </label>
                          </div>
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          Monthly credit:{' '}
                          <strong>{parseFloat(effectiveLeavePreview.breakdown.monthly_leave_balance.monthly_credit || 0).toFixed(1)}</strong>
                        </div>
                        <div>
                          Used:{' '}
                          <strong>{parseFloat(effectiveLeavePreview.breakdown.monthly_leave_balance.used_days || 0).toFixed(1)}</strong>
                        </div>
                        <div>
                          Remaining:{' '}
                          <strong>{parseFloat(effectiveLeavePreview.breakdown.monthly_leave_balance.remaining ?? 0).toFixed(1)}</strong>
                        </div>
                        <div>
                          {effectiveLeavePreview.breakdown.monthly_leave_balance.in_probation ? 'Probation deduct:' : 'Over balance:'}{' '}
                          <strong className={parseFloat(effectiveLeavePreview.excess_leave_days || 0) > 0 ? 'text-red-600' : ''}>
                            {parseFloat(effectiveLeavePreview.excess_leave_days || 0).toFixed(1)}
                          </strong>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600">
                        Estimated leave deduction:{' '}
                        <span className="font-semibold text-red-600">
                          {formatInr(effectiveLeavePreview.leaveDeduction)}
                        </span>
                        {parseFloat(effectiveLeavePreview.excess_leave_days || 0) > 0 && (
                          <span className="text-red-700">
                            {' '}
                            (includes {parseFloat(effectiveLeavePreview.excess_leave_days).toFixed(1)} day
                            {effectiveLeavePreview.excess_leave_days === 1 ? '' : 's'}
                            {effectiveLeavePreview.breakdown.monthly_leave_balance.in_probation
                              ? ' during probation'
                              : effectiveLeavePreview.leave_overflow_settlement === 'salary_deduct'
                                ? ' over monthly credit'
                                : ' over balance'})
                          </span>
                        )}
                      </p>
                      {parseFloat(effectiveLeavePreview.leaveDeduction || 0) > 0 &&
                        parseFloat(effectiveLeavePreview.excess_leave_days || 0) === 0 &&
                        Array.isArray(effectiveLeavePreview.breakdown?.leave_type_breakdown) &&
                        effectiveLeavePreview.breakdown.leave_type_breakdown.length > 0 && (
                          <ul className="text-xs text-gray-600 list-disc list-inside space-y-0.5">
                            {effectiveLeavePreview.breakdown.leave_type_breakdown
                              .filter((row) => parseFloat(row.deduction || 0) > 0)
                              .map((row) => (
                                <li key={row.leave_type}>
                                  {row.leave_type}: {parseFloat(row.days || 0).toFixed(1)} day
                                  {parseFloat(row.days || 0) === 1 ? '' : 's'} ({formatInr(row.deduction)})
                                </li>
                              ))}
                          </ul>
                        )}
                    </>
                  ) : (
                    <p className="text-xs text-gray-500">
                      Estimated leave deduction:{' '}
                      <span className="font-semibold text-red-600">{formatInr(effectiveLeavePreview?.leaveDeduction)}</span>
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3 flex-shrink-0 bg-white rounded-b-xl">
              <button
                onClick={() => setShowGenerateModal(false)}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerate}
                disabled={!generateData.user_id || !generateData.basic_salary}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Generate Payroll
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedPayroll && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold">Mark Payment as Paid</h3>
              <p className="text-sm text-gray-500 mt-1">
                {selectedPayroll.full_name} - {formatMonthLongFromIndex0(selectedPayroll.month - 1)} {selectedPayroll.year}
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={paymentData.payment_date}
                  onChange={(e) => setPaymentData({ ...paymentData, payment_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Method <span className="text-red-500">*</span>
                </label>
                <select
                  value={paymentData.payment_mode}
                  onChange={(e) => setPaymentData({ ...paymentData, payment_mode: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="cash">Cash</option>
                  <option value="cheque">Cheque</option>
                  <option value="upi">UPI</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={paymentData.notes}
                  onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  placeholder="Optional payment notes (transaction ID, reference number, etc.)"
                />
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Net Salary:</span>
                  <span className="font-semibold text-primary-600">
                    ₹{parseFloat(selectedPayroll.net_salary).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  setSelectedPayroll(null);
                }}
                className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdatePayment}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Mark as Paid
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Leave Deduction Breakdown Modal */}
      {showBreakdownModal && breakdownData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold">Leave Deduction Breakdown</h3>
              <p className="text-sm text-gray-500 mt-1">
                {breakdownData.staffName} - {breakdownData.period}
              </p>
            </div>
            <div className="p-6">
              <div className="mb-4 space-y-3">
                <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                  <span className="font-medium text-gray-700">Per Day Salary:</span>
                  <span className="font-semibold text-blue-700">₹{parseFloat(breakdownData.perDaySalary || 0).toFixed(2)}</span>
                </div>
                {breakdownData.breakdown.monthly_leave_balance && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                    <p className="font-semibold text-amber-900 mb-2">
                      Monthly leave — {breakdownData.breakdown.monthly_leave_balance.leave_type_name}
                      {breakdownData.breakdown.monthly_leave_balance.in_probation && (
                        <span className="ml-2 text-xs bg-amber-200 text-amber-900 px-2 py-0.5 rounded-full">Probation</span>
                      )}
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-xs text-amber-900">
                      <div>Carry-in: <strong>{parseFloat(breakdownData.breakdown.monthly_leave_balance.carry_in || 0).toFixed(1)}</strong></div>
                      <div>Credit: <strong>{parseFloat(breakdownData.breakdown.monthly_leave_balance.monthly_credit || 0).toFixed(1)}</strong></div>
                      <div>Used: <strong>{parseFloat(breakdownData.breakdown.monthly_leave_balance.used_days || 0).toFixed(1)}</strong></div>
                      <div>Carry-out: <strong>{parseFloat(breakdownData.breakdown.monthly_leave_balance.carry_forward_out ?? breakdownData.breakdown.monthly_leave_balance.remaining ?? 0).toFixed(1)}</strong></div>
                      <div>
                        {breakdownData.breakdown.monthly_leave_balance.in_probation ? 'Probation deduct:' : 'Salary excess:'}{' '}
                        <strong className="text-red-700">
                          {parseFloat(breakdownData.breakdown.excess_leave_days || 0).toFixed(1)}
                        </strong>
                      </div>
                    </div>
                    {breakdownData.breakdown.leave_overflow_settlement && (
                      <p className="text-xs text-amber-800 mt-2">
                        Overflow settlement:{' '}
                        <strong>
                          {breakdownData.breakdown.leave_overflow_settlement === 'salary_deduct'
                            ? 'Deducted from salary'
                            : 'Used previous leave quota'}
                        </strong>
                      </p>
                    )}
                    {parseFloat(breakdownData.breakdown.excess_leave_days || 0) > 0 && (
                      <p className="text-xs text-red-700 mt-2">
                        {breakdownData.breakdown.monthly_leave_balance.in_probation ? 'Probation leave deduction' : 'Excess deduction'}:{' '}
                        {formatInr(breakdownData.breakdown.excess_leave_deduction)}
                      </p>
                    )}
                  </div>
                )}
              </div>
              
              {breakdownData.breakdown.leave_type_breakdown && breakdownData.breakdown.leave_type_breakdown.length > 0 ? (
                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-900 mb-3">Deduction Details:</h4>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left p-3 font-semibold text-gray-700">Leave Type</th>
                          <th className="text-center p-3 font-semibold text-gray-700">Count</th>
                          <th className="text-center p-3 font-semibold text-gray-700">Days</th>
                          <th className="text-right p-3 font-semibold text-gray-700">Deduction (₹)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {breakdownData.breakdown.leave_type_breakdown.map((item, idx) => (
                          <tr key={idx} className="border-b border-gray-100">
                            <td className="p-3 font-medium text-gray-900">{item.leave_type}</td>
                            <td className="p-3 text-center text-gray-600">{item.count}</td>
                            <td className="p-3 text-center text-gray-600">{parseFloat(item.days).toFixed(1)}</td>
                            <td className="p-3 text-right font-semibold text-red-600">
                              -₹{parseFloat(item.deduction).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-gray-50 font-semibold">
                          <td className="p-3 text-gray-900" colSpan="2">Total Deductible Days:</td>
                          <td className="p-3 text-center text-gray-900">
                            {parseFloat(breakdownData.breakdown.total_deductible_days || 0).toFixed(1)}
                          </td>
                          <td className="p-3 text-right text-red-700">
                            -₹{breakdownData.breakdown.leave_type_breakdown.reduce(
                              (sum, item) => sum + (parseFloat(item.deduction) || 0),
                              0
                            ).toFixed(2)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-gray-600">Present Days:</span>
                        <span className="ml-2 font-medium text-green-600">{breakdownData.breakdown.present_days || 0}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Paid Leaves:</span>
                        <span className="ml-2 font-medium text-blue-600">{breakdownData.breakdown.paid_leaves || 0}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Holidays:</span>
                        <span className="ml-2 font-medium text-purple-600">{breakdownData.breakdown.holidays || 0}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Half Days:</span>
                        <span className="ml-2 font-medium text-yellow-600">{breakdownData.breakdown.half_days || 0}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No leave deductions for this period
                </div>
              )}
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => {
                  setShowBreakdownModal(false);
                  setBreakdownData(null);
                }}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Salary slip — reference layout (Staff → Payroll → eye) */}
      {showViewModal && viewPayroll && (() => {
        const basic = parseFloat(viewPayroll.basic_salary) || 0;
        const allowances = parseFloat(viewPayroll.allowances) || 0;
        const bonus = parseFloat(viewPayroll.bonus) || 0;
        const overtimeAmt = parseFloat(viewPayroll.overtime_amount) || 0;
        const overtimeHrs = parseFloat(viewPayroll.overtime_hours) || 0;
        const deductions = parseFloat(viewPayroll.deductions) || 0;
        const leaveDed = parseFloat(viewPayroll.leave_deduction) || 0;
        const gross = basic + allowances + bonus + overtimeAmt;
        const totalDeductions = deductions + leaveDed;
        const net = parseFloat(viewPayroll.net_salary) || 0;
        const clinicLabel = currentClinic?.name || 'Clinic';
        const clinicAddress =
          (currentClinic?.address && String(currentClinic.address).trim()) || DEFAULT_CLINIC_PAYSLIP_ADDRESS;
        const clinicPhone =
          (currentClinic?.contact_phone && String(currentClinic.contact_phone).trim()) ||
          DEFAULT_CLINIC_PAYSLIP_PHONE;
        const logoSrc = (() => {
          if (!currentClinic?.logo_path?.trim()) return null;
          const src = resolveClinicLogoSrc(currentClinic.logo_path);
          return src === defaultClinicLogo ? null : src;
        })();

        const slipCell = 'border border-slate-200 px-3 py-2 text-sm text-slate-800';
        const slipAmt = 'border border-slate-200 px-3 py-2 text-sm text-right tabular-nums text-slate-900 font-medium';
        const slipTheadTh = `${slipCell} text-xs font-semibold uppercase tracking-wide text-slate-500 bg-slate-50`;
        const slipTheadAmt = `${slipAmt} text-xs font-semibold uppercase tracking-wide text-slate-500 bg-slate-50`;
        const slipTableWrap = 'w-full border-collapse text-sm [&_tbody_tr:nth-child(even)]:bg-slate-50/40';

        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-slate-900/55 backdrop-blur-[2px]">
            <div className="relative w-full max-w-3xl max-h-[92vh] flex flex-col rounded-xl bg-white shadow-2xl ring-1 ring-slate-200/90 overflow-hidden">
              <button
                type="button"
                aria-label="Close"
                onClick={() => {
                  setShowViewModal(false);
                  setViewPayroll(null);
                  setViewBreakdown(null);
                }}
                className="absolute right-2 top-2 z-10 p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition-colors"
              >
                <HiX className="w-5 h-5" />
              </button>

              <div className="overflow-y-auto flex-1 bg-white">
                {/* Letterhead: Krishna brand + document title */}
                <div className="bg-slate-50 border-b border-slate-200 px-5 sm:px-8 pt-5 pb-5 pr-14">
                  <div className="flex flex-col lg:flex-row lg:items-start gap-6 lg:gap-8">
                    <div className="flex flex-wrap items-end gap-3 sm:gap-4">
                      <div className="rounded-lg bg-white p-2 sm:p-2.5 ring-1 ring-slate-200/80 shadow-sm">
                        <img
                          src={defaultClinicLogo}
                          alt={`${clinicLabel} logo`}
                          className="h-24 sm:h-28 w-auto max-w-[280px] object-contain object-left"
                        />
                      </div>
                      {logoSrc ? (
                        <img
                          src={logoSrc}
                          alt={`${clinicLabel} clinic mark`}
                          className="h-9 sm:h-11 w-auto max-w-[130px] object-contain object-left opacity-95"
                        />
                      ) : null}
                    </div>
                    <div className="flex-1 min-w-0 lg:text-right space-y-2">
                      <h3 className="text-xl sm:text-2xl font-semibold text-slate-900 tracking-tight">
                        Salary payslip
                      </h3>
                      <div className="flex flex-col sm:flex-row sm:flex-wrap lg:justify-end items-start sm:items-center gap-2">
                        <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-700 shadow-sm tabular-nums">
                          {slipPeriodLabel}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 pt-4 border-t border-slate-200/80 space-y-1 text-sm text-slate-700">
                    <p className="text-slate-600 leading-relaxed">{clinicAddress}</p>
                    <p className="text-slate-600 tabular-nums">{clinicPhone}</p>
                  </div>
                </div>

                <div className="px-5 sm:px-8 pb-6 pt-5 space-y-5">
                  {/* Employee Information */}
                  <div className="border border-slate-200 overflow-hidden rounded-lg shadow-sm">
                    <div className={SLIP_SECTION_HEAD}>Employee Information</div>
                    <div className={`${SLIP_CONTENT_INDENT} py-4 sm:py-5 text-sm bg-white`}>
                      <p>
                        <span className="font-semibold text-slate-800">Employee Name: </span>
                        <span className="text-slate-700">{viewPayroll.full_name}</span>
                      </p>
                    </div>
                  </div>

                  {/* Earnings | Deductions */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-0 md:divide-x divide-slate-200 border border-slate-200 rounded-lg overflow-hidden shadow-sm">
                    <div className="bg-white min-w-0">
                      <div className={SLIP_SECTION_HEAD}>Earnings</div>
                      <div className={SLIP_CONTENT_INDENT}>
                      <table className={slipTableWrap}>
                        <thead>
                          <tr>
                            <th className={slipTheadTh}>Description</th>
                            <th className={slipTheadAmt}>Amount (Rs.)</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className={slipCell}>Basic Salary</td>
                            <td className={slipAmt}>{formatInr(basic)}</td>
                          </tr>
                          <tr>
                            <td className={slipCell}>House Rent Allowance / Allowances</td>
                            <td className={slipAmt}>{formatInr(allowances)}</td>
                          </tr>
                          <tr>
                            <td className={slipCell}>Bonus</td>
                            <td className={slipAmt}>{formatInr(bonus)}</td>
                          </tr>
                          {(overtimeAmt > 0 || overtimeHrs > 0) && (
                            <tr>
                              <td className={slipCell}>
                                Overtime{overtimeHrs > 0 ? ` (${overtimeHrs} h)` : ''}
                              </td>
                              <td className={slipAmt}>{formatInr(overtimeAmt)}</td>
                            </tr>
                          )}
                          <tr className="bg-slate-50 font-bold">
                            <td className={slipCell}>Gross Salary</td>
                            <td className={slipAmt}>{formatInr(gross)}</td>
                          </tr>
                        </tbody>
                      </table>
                      </div>
                    </div>
                    <div className="bg-white min-w-0 border-t md:border-t-0 border-slate-200">
                      <div className={SLIP_SECTION_HEAD}>Deductions</div>
                      <div className={SLIP_CONTENT_INDENT}>
                      <table className={slipTableWrap}>
                        <thead>
                          <tr>
                            <th className={slipTheadTh}>Description</th>
                            <th className={slipTheadAmt}>Amount (Rs.)</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className={slipCell}>Unpaid Leave Deduction</td>
                            <td className={slipAmt}>{formatInr(leaveDed)}</td>
                          </tr>
                          <tr>
                            <td className={slipCell}>Professional Tax / Other deductions</td>
                            <td className={slipAmt}>{formatInr(deductions)}</td>
                          </tr>
                          <tr className="bg-slate-50 font-bold">
                            <td className={slipCell}>Total Deductions</td>
                            <td className={slipAmt}>{formatInr(totalDeductions)}</td>
                          </tr>
                        </tbody>
                      </table>
                      </div>
                    </div>
                  </div>

                  {/* NET PAY */}
                  <div className={`flex flex-wrap items-center justify-between gap-2 px-4 py-3.5 sm:px-6 sm:py-4 ${SLIP_BLUE} text-white shadow-md rounded-lg`}>
                    <span className="text-sm sm:text-base font-bold uppercase tracking-wide">Net Pay</span>
                    <span className="text-xl sm:text-2xl font-bold tabular-nums">{formatInr(net)}</span>
                  </div>


                  <p className="mx-auto max-w-prose text-center text-[11px] text-slate-500 px-2 pt-6 mt-1 leading-relaxed border-t border-slate-100">
                    This is a computer-generated payslip and does not require a signature. For any queries, please contact your clinic
                    administrator.
                  </p>
                </div>
              </div>

              <div className="flex-shrink-0 border-t border-slate-200 bg-white px-5 sm:px-8 py-4 flex flex-col-reverse sm:flex-row sm:flex-wrap sm:justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowViewModal(false);
                    setViewPayroll(null);
                    setViewBreakdown(null);
                  }}
                  className="w-full sm:w-auto px-4 py-2.5 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={() => handleDownloadSlip(viewPayroll.id)}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#4A69E1] text-white text-sm font-semibold hover:opacity-95 shadow-sm"
                >
                  <HiDownload className="w-4 h-4 shrink-0" />
                  Download PDF
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default Payroll;

